/**
 * How Saidiseire's numbers read on screen. Pure: no React, no fetch. What a
 * site's state means on the desk (pill, chip, order) is `monitoringState.ts`.
 *
 * Every rule here is one a screen would otherwise decide inline, and each has
 * a way of being quietly wrong that a test can catch and a glance cannot:
 *
 *  - an uptime of 99.996 rounded to "100%" tells the reader a site with an
 *    outage had none, so the figure is floored, never rounded;
 *  - a day with no rollup is not a good day, so the strip draws it as unknown
 *    rather than borrowing its neighbour's colour;
 *  - a response time is only honest from answers that loaded - a fast error
 *    page pulls a median down, and a timeout has no time at all - so outages
 *    are spans instead of points (`downSpans`), and the response chart's
 *    times come from `up` checks only (`monitoringSeries.ts`).
 *
 * Imported through `monitoring.ts`, which re-exports this file.
 */
import type { StatusTone } from "../crm/ui";
import type { MonitorCheckView, MonitorDayView, MonitorEngineStatus, MonitorExpiryView } from "./monitoring";

// ── Numbers ──────────────────────────────────────────────────────────────────

const pct = (v: number, digits: number) =>
  `${v.toLocaleString("et-EE", { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;

/**
 * Uptime as a percentage, or "-" when nothing was counted.
 *
 * Floored rather than rounded: 99.996 must not print as 100%. Two decimals from
 * 99 up, because that is where the difference between a good month and a bad
 * one lives (99.9 is 43 minutes down in 30 days, 99.99 is four).
 */
export function formatUptime(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "-";
  if (value >= 100) return "100%";
  const digits = value >= 99 ? 2 : 1;
  const step = 10 ** digits;
  return pct(Math.max(0, Math.floor(value * step) / step), digits);
}

/** Time to first byte: milliseconds below a second, seconds with one decimal above. */
export function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "-";
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`;
  return `${(Math.round(ms / 100) / 10).toLocaleString("et-EE", { minimumFractionDigits: 1 })} s`;
}

export interface DurationUnits {
  d: string;
  h: string;
  min: string;
}

/** An outage's length in the two largest units that apply: "2 p 3 h", "1 h 5 min",
 *  "27 min". Anything under a minute reads "< 1 min" rather than "0 min". */
export function formatDuration(seconds: number, units: DurationUnits): string {
  const total = Math.max(0, Math.floor(seconds / 60));
  if (total === 0) return `< 1 ${units.min}`;
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  if (d > 0) return h > 0 ? `${d} ${units.d} ${h} ${units.h}` : `${d} ${units.d}`;
  if (h > 0) return m > 0 ? `${h} ${units.h} ${m} ${units.min}` : `${h} ${units.h}`;
  return `${m} ${units.min}`;
}

// ── What a check said ────────────────────────────────────────────────────────

/** Every code the engine writes that has its own sentence under `monitoring.code`.
 *  `http_<status>` is read by pattern instead; anything else is shown raw. */
export const CHECK_CODES = [
  "dns_failed",
  "timeout",
  "refused",
  "reset",
  "unreachable_host",
  "challenge",
  "blocked",
  "deadline",
  "body_timeout",
  "redirect_loop",
  "config_private_address",
  "config_bad_url",
  "config_bad_protocol",
  "cert_expired",
  "cert_not_yet_valid",
  "cert_wrong_host",
  "cert_untrusted",
  "tls_or_protocol",
  "our_network",
  "our_address_blocked",
  "our_resolver",
  "suspended",
  "empty_page",
  "flapping",
] as const;

const KNOWN_CODES = new Set<string>(CHECK_CODES);

/** The sentence for a check code: a key plus its variables, or null for no code
 *  or one this browser does not know (the screen then shows the code itself). */
export function codeMessage(code: string | null | undefined): { key: string; vars?: { status: number } } | null {
  if (!code) return null;
  const http = /^http_(\d{3})$/.exec(code);
  if (http) return { key: "monitoring.code.http", vars: { status: Number(http[1]) } };
  return KNOWN_CODES.has(code) ? { key: `monitoring.code.${code}` } : null;
}

// ── The 90-day strip ─────────────────────────────────────────────────────────

/** Below this a day with downtime is drawn as down rather than as a partial day. */
export const STRIP_DOWN_BELOW = 95;

export type StripTone = "up" | "partial" | "down" | "unknown";

export interface StripCell {
  day: string;
  tone: StripTone;
  /** The rollup, or null for a day nothing was recorded on. */
  row: MonitorDayView | null;
}

/** "YYYY-MM-DD" for an instant, as the calendar reads in Tallinn - the same
 *  day boundary the rollups are keyed by. */
export function tallinnDayKey(at: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Tallinn",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/** Calendar arithmetic on a day key. Done in UTC on the date alone, so a DST
 *  change (a 23- or 25-hour day in Tallinn) cannot skip or repeat a key. */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

export function dayTone(row: MonitorDayView | null): StripTone {
  if (!row || row.uptime === null) return "unknown";
  if (row.uptime >= 100 && row.downSeconds === 0) return "up";
  return row.uptime < STRIP_DOWN_BELOW ? "down" : "partial";
}

/**
 * One cell per calendar day, oldest first, ending on `today`.
 *
 * The server sends only days that have a rollup. A missing day is drawn as
 * unknown - never interpolated - because "we have no record" and "it was fine"
 * are different claims, and the first is what a gap actually says: the engine
 * was off, the site was paused, or it was not watched yet.
 */
export function uptimeStrip(days: readonly MonitorDayView[], today: string, count = 90): StripCell[] {
  const byDay = new Map(days.map((d) => [d.day, d]));
  const cells: StripCell[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const day = addDays(today, -i);
    const row = byDay.get(day) ?? null;
    cells.push({ day, tone: dayTone(row), row });
  }
  return cells;
}

// ── Certificate and domain expiry ────────────────────────────────────────────

/** The notice thresholds the engine mails at, largest first. */
export const EXPIRY_NOTICE_DAYS = { cert: [14, 7, 3], domain: [30, 14, 7] } as const;
export type ExpiryKind = keyof typeof EXPIRY_NOTICE_DAYS;

export type ExpiryLevel = "ok" | "soon" | "urgent" | "expired" | "unknown";

/**
 * How close an expiry is. Inside the largest threshold it is "soon", inside the
 * smallest "urgent", past it "expired". No date is "unknown", which is what a
 * failed registry or certificate look leaves - never "fine".
 */
export function expiryLevel(view: MonitorExpiryView | null, kind: ExpiryKind): ExpiryLevel {
  if (!view || view.daysLeft === null) return "unknown";
  const steps = EXPIRY_NOTICE_DAYS[kind];
  if (view.daysLeft < 0) return "expired";
  if (view.daysLeft <= steps[steps.length - 1]!) return "urgent";
  if (view.daysLeft <= steps[0]) return "soon";
  return "ok";
}

export function expiryTone(level: ExpiryLevel): StatusTone {
  if (level === "ok") return "ok";
  if (level === "soon") return "wt";
  if (level === "unknown") return "mut";
  return "lt";
}

/** The key under `monitoring.expiry` for the days-left wording. */
export function expiryKey(view: MonitorExpiryView | null): "expired" | "today" | "inDays" | "unknown" {
  if (!view || view.daysLeft === null) return "unknown";
  if (view.daysLeft < 0) return "expired";
  if (view.daysLeft === 0) return "today";
  return "inDays";
}

/** Certificate verdicts with a sentence of their own under `monitoring.cert.verdict`. */
const CERT_VERDICTS = new Set(["valid", "expired", "not_yet_valid", "wrong_host", "untrusted", "unchecked"]);

export function certVerdictKey(verdict: string | null): string | null {
  return verdict && CERT_VERDICTS.has(verdict) ? `monitoring.cert.verdict.${verdict}` : null;
}

// ── Down spans: the rule the series route mirrors ────────────────────────────

/**
 * The stretches the site was seen down, merged: from the first failing check
 * to the check that next said something else (or to the last failure when
 * nothing came after it). Blocked and unknown checks end a span - they are not
 * evidence of an outage.
 *
 * The response chart no longer draws from this: its spans come from the
 * server (`seriesDownSpans` in `apps/api/src/modules/crm-monitoring/lib/series.ts`),
 * which mirrors this rule and names it as its reference. Kept, with its test,
 * so the rule has one statement on the CRM's side to be held against.
 */
export function downSpans(checks: readonly MonitorCheckView[]): { from: number; to: number }[] {
  const spans: { from: number; to: number }[] = [];
  let open: { from: number; to: number } | null = null;
  for (const c of checks) {
    const t = Date.parse(c.at);
    if (c.outcome === "down") {
      if (open) open.to = t;
      else open = { from: t, to: t };
      continue;
    }
    if (open) {
      open.to = t;
      spans.push(open);
      open = null;
    }
  }
  if (open) spans.push(open);
  return spans;
}

// ── The engine ───────────────────────────────────────────────────────────────

/**
 * Silence longer than this from a switched-on engine means it has stopped. The
 * one threshold for every screen that says so: the desk's strip here and the
 * shell's background card (`STALE_AFTER_MS.monitoring`), so the two never
 * disagree about the same beat.
 *
 * The tick runs every minute and beats at its end. Its worst honest length is
 * about five minutes: no new probe after 45 s, the probes then in flight (a
 * 15 s recheck, a 15 s certificate look, a second look at a 403), and in a mass
 * outage an outside look of up to 30 s per confirmation, a few at a time. Ten
 * minutes of silence is past any tick that is still running.
 */
export const ENGINE_LATE_MS = 10 * 60_000;

export type EngineHealth = "off" | "waiting" | "ok" | "failing" | "late";

export function engineHealth(engine: MonitorEngineStatus, now: number): EngineHealth {
  if (!engine.enabled) return "off";
  if (!engine.lastBeatAt) return "waiting";
  const at = Date.parse(engine.lastBeatAt);
  if (Number.isFinite(at) && now - at > ENGINE_LATE_MS) return "late";
  return engine.lastBeatOk === false ? "failing" : "ok";
}

/**
 * Why the last round failed, when the engine's note is one with a sentence of
 * its own under `monitoring.engine`. The notes are the worker beat's English
 * shorthand (`crm-monitoring/lib/sweep.ts`, `beatNote`). "our network" is the
 * one an operator can read something into - our side was down, not the
 * customers' - and an internal error message is shown as it came, because it
 * is a bug report rather than a sentence.
 */
export function beatNoteKey(note: string | null): "failingOurNetwork" | null {
  return note === "our network" ? "failingOurNetwork" : null;
}
